import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export default function FacilityGallery({ images = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  
  // Ensure we have at least one image
  const allImages = images.length > 0 ? images : ['/placeholder-facility.jpg'];
  
  // Get main image and thumbnails
  const mainImage = allImages[activeIndex];
  const thumbnails = allImages.slice(0, 5); // Show up to 5 thumbnails
  
  return (
    <div className="relative">
      {/* Main image */}
      <div 
        className="relative h-[400px] overflow-hidden rounded-lg cursor-pointer"
        onClick={() => setShowGallery(true)}
      >
        <img 
          src={mainImage} 
          alt="Facility" 
          className="w-full h-full object-cover"
        />
        
        {/* View all photos button */}
        <div className="absolute bottom-4 right-4">
          <Button 
            variant="secondary" 
            className="bg-white/90 hover:bg-white text-gray-800 font-medium shadow-md"
            onClick={(e) => {
              e.stopPropagation();
              setShowGallery(true);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            View All Photos ({allImages.length})
          </Button>
        </div>
      </div>
      
      {/* Thumbnails */}
      {allImages.length > 1 && (
        <div className="flex space-x-2 mt-2 overflow-x-auto pb-1">
          {thumbnails.map((image, index) => (
            <button
              key={index}
              className={`relative w-20 h-20 rounded-md overflow-hidden flex-shrink-0 ${
                index === activeIndex ? 'ring-2 ring-blue-600' : ''
              }`}
              onClick={() => setActiveIndex(index)}
            >
              <img 
                src={image} 
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
          
          {allImages.length > 5 && (
            <button
              className="relative w-20 h-20 rounded-md overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200"
              onClick={() => setShowGallery(true)}
            >
              +{allImages.length - 5} more
            </button>
          )}
        </div>
      )}
      
      {/* Full gallery modal */}
      <Dialog open={showGallery} onOpenChange={setShowGallery}>
        <DialogContent className="max-w-5xl p-0 bg-black/95">
          <div className="relative h-[80vh]">
            {/* Gallery navigation */}
            <div className="absolute inset-0 flex items-center justify-between px-4 z-10">
              <Button
                variant="ghost"
                className="h-12 w-12 rounded-full bg-black/50 text-white hover:bg-black/70"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Button>
              
              <Button
                variant="ghost"
                className="h-12 w-12 rounded-full bg-black/50 text-white hover:bg-black/70"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
            
            {/* Main gallery image */}
            <div className="h-full flex items-center justify-center">
              <img 
                src={allImages[activeIndex]} 
                alt={`Gallery image ${activeIndex + 1}`}
                className="max-h-full max-w-full object-contain"
              />
            </div>
            
            {/* Image counter */}
            <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
              {activeIndex + 1} / {allImages.length}
            </div>
            
            {/* Close button */}
            <Button
              variant="ghost"
              className="absolute top-4 right-4 h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
              onClick={() => setShowGallery(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
          
          {/* Thumbnails */}
          <div className="bg-black p-4 flex space-x-2 overflow-x-auto">
            {allImages.map((image, index) => (
              <button
                key={index}
                className={`relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 ${
                  index === activeIndex ? 'ring-2 ring-blue-600' : ''
                }`}
                onClick={() => setActiveIndex(index)}
              >
                <img 
                  src={image} 
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
